/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { TimelineTypes } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TimelineProps {
  id: string;
  name: string;
  companyId: string | null;
  activationRuleId: string | null;
  timelineType: TimelineTypes | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TimelineRecord extends Record<TimelineProps>({
  id: null as any,
  name: null as any,
  companyId: null as any,
  activationRuleId: null as any,
  timelineType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    companyId: string | null;
    activationRuleId: string | null;
    timelineType: TimelineTypes | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TimelineRecord {
    if (json == null) {
      throw new Error('TimelineRecord.fromJS: parameter cannot be null');
    }
    return new TimelineRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TimelineJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'TimelineJson.name'),
      companyId: json.companyId,
      activationRuleId: json.activationRuleId,
      timelineType: json.timelineType,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    companyId: string | null;
    activationRuleId: string | null;
    timelineType: TimelineTypes | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TimelineRecord, Error[]] {
    if (json == null) {
      throw new Error('TimelineRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TimelineRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TimelineJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'TimelineJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () => json.companyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activationRuleId: tryOrDefault(
        () => json.activationRuleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timelineType: tryOrDefault(
        () => json.timelineType,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TimelineProps) {
    super(props);
    if (props == null) {
      throw new Error('TimelineRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TimelineViewModelBase implements TimelineProps {
  protected readonly _props: TimelineProps;
  private _json?: TimelineProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get companyId(): string | null {
    return this._props.companyId;
  }
  get activationRuleId(): string | null {
    return this._props.activationRuleId;
  }
  get timelineType(): TimelineTypes | null {
    return this._props.timelineType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isContact() {
    return this._props.timelineType === TimelineTypes.Contact;
  }

  public isAnyTimelineType(...timelineTypes: TimelineTypes[]) {
    return this._props.timelineType != null && timelineTypes.includes(this._props.timelineType);
  }

  constructor(props: TimelineProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        companyId: this._props.companyId,
        activationRuleId: this._props.activationRuleId,
        timelineType: this._props.timelineType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTimelineEntities = (state: AppState) => state.entityStores.timeline.entities;

export const selectTimelineById = (id: string) => (state: AppState) =>
  state.entityStores.timeline.entities.get(id);

export const selectTimelineMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.timeline.meta.get(id);

export const selectTimelineAndMetadataById = (id: string) =>
  createSelector(selectTimelineById(id), selectTimelineMetadataById(id), (timeline, meta) => ({
    timeline,
    meta,
  }));

export function selectTimelineEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTimelineEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TimelineRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTimelineEntitiesByName(name: string) {
  return createSelector(selectAllTimelineEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectTimelineEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTimelineEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectTimelineEntitiesByActivationRuleId(activationRuleId: string) {
  return createSelector(selectAllTimelineEntities, (entities) =>
    entities.filter((e) => e.activationRuleId === activationRuleId).valueSeq(),
  );
}

export function selectTimelineEntitiesByTimelineType(timelineType: TimelineTypes) {
  return createSelector(selectAllTimelineEntities, (entities) =>
    entities.filter((e) => e.timelineType === timelineType).valueSeq(),
  );
}
//#endregion Selectors
